import { useMemo } from 'react';
import { gql, useQuery } from 'urql';

export const GET_TICKET_SEGMENTS_QUERY = gql`
  query GetTicketSegments($eventId: Int!, $input: TicketsFilter!) {
    getTicketSegments(eventId: $eventId, input: $input) {
      id
      title
      description
      eventId
      countTicket
      tickets {
        id
        checked
        organizerName
        ticketSegmentId
        checkInTime
        checkedBy
        scanType
        smsTicketSent
        emailTicketSent
        smsInviteSent
        emailInviteSent
        source
        invitationStatus
        ticketIsSentAfterAcception
        dependents {
          countExtraChildren
          countExtraAdult
          adult {
            id
            checked
            invitationStatus
            smsInviteSent
            emailInviteSent
            smsTicketSent
            emailTicketSent
            checkInTime
            organizerName
            scanType
            participant {
              id
              position
              firstName
              lastName
              active
              phone
              email
            }
          }
          child {
            id
            checked
            invitationStatus
            smsInviteSent
            emailInviteSent
            smsTicketSent
            emailTicketSent
            checkInTime
            organizerName
            scanType
            participant {
              id
              position
              firstName
              lastName
              active
              phone
              email
            }
          }
        }
        participant {
          id
          position
          firstName
          lastName
          sortNumber
          active
          surname
          source
          isIndividual
          joinedAt
          leftAt
          phone
          email
        }
        categories {
          title
          color
        }
      }
    }
  }
`;

export type ChannelEnum = 'SMS' | 'EMAIL' | 'sms' | 'email';
export enum InvitationStatusTicketEnum {
  PENDING = 'PENDING',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  EXPIRED = 'EXPIRED',
}

export interface OrderTicketsBy {
  field: string;
  direction: 'asc' | 'desc';
}

export interface InviteFilterInput {
  inviteSentMethod?: ChannelEnum | null;
  inviteSent?: boolean | null;
  invitationStatus?: InvitationStatusTicketEnum | null;
}

export interface TicketFilterInput {
  ticketSentMethod?: ChannelEnum | null;
  ticketSent?: boolean | null;
  checked?: boolean | null;
}

export interface TicketsFilterInput {
  inviteFilter?: InviteFilterInput[] | null;
  ticketFilter?: TicketFilterInput[] | null;
  orderBy?: OrderTicketsBy | null;
  guestFilter?: string | null;
}

export interface IParticipant {
  id: number;
  email?: string | null;
  login?: string | null;
  position?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  sortNumber: number;
  active: boolean;
  surname?: string | null;
  source?: string | null;
  isIndividual: boolean;
  joinedAt?: string | null;
}

export interface CategoryData {
  title: string;
  color: string | null;
}

export interface Ticket {
  id: number;
  checked: boolean;
  organizerName?: string | null;
  ticketSegmentId?: number | null;
  checkInTime?: string | null;
  checkedBy?: number | null;
  scanType?: string | null;
  smsTicketSent?: boolean | null;
  emailTicketSent?: boolean | null;
  smsInviteSent?: boolean | null;
  emailInviteSent?: boolean | null;
  source?: string;
  invitationStatus?: InvitationStatusTicketEnum;
  ticketIsSentAfterAcception?: boolean;
  dependents?: TicketDependents;
  participant: IParticipant;
  categories?: CategoryData[];
  ticketUuid?: string;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface TicketDependents {
  countExtraChildren: number;
  countExtraAdult: number;
  adult: Ticket[];
  child: Ticket[];
}

export interface ISegment {
  id: number;
  title: string;
  eventId?: number;
  countTicket: number;
  tickets?: Ticket[];
}

export enum GUEST_FILTER {
  NOT_ALONE = 'notAlone',
  WITHOUT_CHILDREN = 'withoutChildren',
  WITH_CHILDREN = 'withChildren',
}

export const useFetchTicketSegments = (eventId: number, input?: TicketsFilterInput) => {
  const variables = useMemo(() => {
    const fallback: TicketsFilterInput = {
      inviteFilter: null,
      ticketFilter: null,
      orderBy: null,
      guestFilter: null,
    };

    return {
      eventId,
      input: input ?? fallback,
    };
  }, [eventId, input]);

  const [{ data, error, fetching }, executeQuery] = useQuery({
    query: GET_TICKET_SEGMENTS_QUERY,
    variables,
    pause: !eventId,
    requestPolicy: 'network-only',
  });
  const segments = useMemo(() => data?.getTicketSegments ?? [], [data?.getTicketSegments]);

  return {
    segments,
    error,
    fetching,
    refetch: () => executeQuery({ requestPolicy: 'network-only' }),
  };
};
